import { AlertTriangle } from "lucide-react";
import { Alert, Button, Modal } from "@/components/common";
import { useDeleteUser } from "@/hooks/useAdmin";
import type { ManagedUser } from "@/types/rbac";

interface DeleteUserModalProps {
  user: ManagedUser | null;
  onClose: () => void;
}

export function DeleteUserModal({ user, onClose }: DeleteUserModalProps) {
  const deleteUser = useDeleteUser();

  function handleClose() {
    deleteUser.reset();
    onClose();
  }

  function handleConfirm() {
    if (!user) {
      return;
    }
    deleteUser.mutate(user.id, { onSuccess: handleClose });
  }

  return (
    <Modal
      isOpen={user !== null}
      onClose={handleClose}
      title="Usuń agenta"
    >
      <div className="space-y-4">
        {deleteUser.isError && (
          <Alert variant="error">
            {(deleteUser.error as { message?: string })?.message ??
              "Nie udało się usunąć agenta"}
          </Alert>
        )}

        <div className="flex gap-3 rounded-lg bg-red-50 p-3 dark:bg-red-900/20">
          <AlertTriangle
            size={20}
            className="shrink-0 text-red-600 dark:text-red-400"
            aria-hidden="true"
          />
          <div className="min-w-0 text-sm text-red-800 dark:text-red-300">
            <p className="font-medium">Ta operacja jest nieodwracalna.</p>
            <p className="mt-1 break-words">
              Agent{" "}
              <span className="font-semibold">
                {user?.firstName} {user?.lastName}
              </span>{" "}
              ({user?.email}) zostanie trwale usunięty z systemu.
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-3 border-t border-edge-soft pt-4">
          <Button variant="ghost" onClick={handleClose}>
            Anuluj
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirm}
            isLoading={deleteUser.isPending}
          >
            Usuń agenta
          </Button>
        </div>
      </div>
    </Modal>
  );
}
